import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { CampaignFeasibilityResponse } from '../../services/feasibilityService';
import {
  formatExtensionDateCopy,
  formatTotalRatioCopy,
} from './shared/feasibilityCopy';

interface InventoryRegressionModalProps {
  verdict: CampaignFeasibilityResponse;
  onEditCampaign: () => void;
  onDismiss: () => void;
  accentColor: string;
}

/**
 * Opened when the submit-time inventory re-check finds the campaign is no
 * longer deliverable. Blocks payment authorization until the buyer edits
 * the campaign. No "force pay" escape hatch — the campaign genuinely won't
 * deliver in its current form.
 *
 * CTV-474: copy matches InventoryWarningCard (Option C totals format) so the
 * buyer sees the same impression-ratio framing in both surfaces.
 */
const InventoryRegressionModal: React.FC<InventoryRegressionModalProps> = ({
  verdict,
  onEditCampaign,
  onDismiss,
  accentColor,
}) => {
  const extensionDateCopy = formatExtensionDateCopy(verdict.extensionDate);
  const totalCopy = formatTotalRatioCopy(verdict);
  // CTV-474: same copy contract as InventoryWarningCard. The submit-time
  // recheck never invokes the geography block (the buyer has already left
  // step 2 by then), so we surface only the date / inventory remediation.
  const remediationLine = extensionDateCopy
    ? `Extend your end date to ${extensionDateCopy} for full delivery.`
    : `Try pushing back your end date further or reducing your budget to fit inventory constraints.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight text-slate-900">
              Inventory changed
            </h2>
            <p className="text-[13px] text-slate-600 mt-1">
              Since you started this campaign, inventory on{' '}
              <strong>{verdict.stationName}</strong> has shifted. Your campaign won't
              fully deliver in its current configuration.
            </p>
          </div>
        </div>

        <div className="text-[12px] text-slate-700 bg-slate-50 rounded-lg p-3 mb-5 space-y-2">
          <p className="text-slate-600">{totalCopy}</p>
          <p className="text-slate-600">{remediationLine}</p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className="px-4 py-2 rounded-full text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onEditCampaign}
            className="px-5 py-2 rounded-full text-[13px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
            style={{ background: accentColor }}
          >
            Edit campaign
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryRegressionModal;
